// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { PropsWithChildren } from 'react';
import { Close } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  ButtonProps,
} from '@mui/material';
import { AsyncButton } from '../Button/AsyncButton';
import { SyncButton } from '../Button/SyncButton';

const FormDialog = ({
  open,
  title,
  onCancel,
  onSubmit,
  submitName,
  children,
  isSubmitDisabled,
  submitButtonSxProps,
}: PropsWithChildren<{
  open: boolean;
  title: string | JSX.Element;
  onCancel: () => void;
  onSubmit: () => Promise<unknown>;
  submitName: string;
  isSubmitDisabled?: boolean;
  submitButtonSxProps?: ButtonProps;
}>) => {
  return (
    <Dialog
      PaperProps={{
        sx: { borderRadius: 0 },
      }}
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <DialogTitle fontWeight="bold">{title}</DialogTitle>
        <Box sx={{ ml: 'auto', mr: 2 }}>
          <IconButton size="small" onClick={onCancel}>
            <Close fontSize="small" color="action" />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ pt: 0 }}>{children}</DialogContent>
      <DialogActions
        sx={{
          pb: 3,
          pl: 3,
          pr: 3,
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <SyncButton sx={{ width: '25%' }} variant="outlined" size="large" onClick={onCancel}>
          Cancel
        </SyncButton>
        <AsyncButton
          sx={{ width: '25%' }}
          size="large"
          color={submitButtonSxProps?.color}
          onClick={onSubmit}
          disabled={isSubmitDisabled}
        >
          {submitName}
        </AsyncButton>
      </DialogActions>
    </Dialog>
  );
};

export default FormDialog;
